import "@xyflow/react/dist/style.css";
import type React from "react";
import type {Edge, NodeTypes} from "@xyflow/react";
import {Background, BackgroundVariant, Controls, Panel, ReactFlow, ReactFlowProvider} from "@xyflow/react";
import {Box, Stack, Typography, useTheme} from "@mui/material";
import {ChangeDiffNodeCard} from "./ChangeDiffNodeCard";
import {type DiffNode, MOVED_EDGE_CLASS, PREVIOUS_EDGE_CLASS} from "./taxonomyChangeDiffGraph";

const NODE_TYPES: NodeTypes = {diff: ChangeDiffNodeCard};
/** Fits the whole picture into the fixed-height canvas: zooms out as far as needed, never in past 100%. */
const FIT_VIEW_OPTIONS = {padding: 0.12, minZoom: 0.1, maxZoom: 1};

/** A short line sample for the legend */
const LineSample: React.FC<{dashed?: boolean}> = ({dashed}) => (
    <Box component="span" sx={{width: 24, borderTop: 2, borderTopStyle: dashed ? 'dashed' : 'solid', borderColor: 'mithraBlue.800'}}/>
);

type ChangeDiffCanvasProps = {
    nodes: DiffNode[];
    edges: Edge[];
    height: number;
};

/**
 * Read-only canvas of one change, before and after in the same picture (see `buildChangeDiffGraph`).
 * The page keeps its scrolling (no wheel zoom); pan by dragging, zoom with the buttons.
 */
export const ChangeDiffCanvas: React.FC<ChangeDiffCanvasProps> = ({nodes, edges, height}) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                height,
                border: 1,
                borderColor: 'grey.100',
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                // Edge classes from the graph: dashed for the previous state, a move's old link darker.
                [`& .${PREVIOUS_EDGE_CLASS} path`]: {strokeDasharray: '6 4'},
                // `!important`: the default edge colour is an inline style.
                [`& .${MOVED_EDGE_CLASS} path`]: {stroke: `${theme.palette.mithraBlue[800]} !important`, opacity: 0.55},
            }}
        >
            <ReactFlowProvider>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={NODE_TYPES}
                    defaultEdgeOptions={{style: {stroke: theme.palette.mithraBlue[500], strokeWidth: 1.5}}}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    nodesFocusable={false}
                    edgesFocusable={false}
                    elementsSelectable={false}
                    zoomOnScroll={false}
                    preventScrolling={false}
                    minZoom={0.1}
                    fitView
                    fitViewOptions={FIT_VIEW_OPTIONS}
                    proOptions={{hideAttribution: true}}
                >
                    <Background variant={BackgroundVariant.Dots} gap={16} size={1}/>
                    <Controls showInteractive={false} position="bottom-left"/>
                    <Panel position="top-left">
                        <Stack direction="row" gap={2} sx={{px: 1, py: 0.5, bgcolor: 'background.paper', borderRadius: 1}}>
                            <Stack direction="row" gap={0.75} alignItems="center">
                                <LineSample/><Typography variant="caption" color="mithraBlue.800">Now</Typography>
                            </Stack>
                            <Stack direction="row" gap={0.75} alignItems="center">
                                <LineSample dashed/><Typography variant="caption" color="mithraBlue.800">Before</Typography>
                            </Stack>
                        </Stack>
                    </Panel>
                </ReactFlow>
            </ReactFlowProvider>
        </Box>
    );
};
